import type { MotionProps, Transition, Variants } from "framer-motion";

// Z-Index system
// IMPORTANT: Must match semanticTokens.zIndex from packages/config/theme.ts (SSOT)
export const Z_INDEX = {
  base: 1,
  elevated: 10,
  dropdown: 20,
  sticky: 30,
  backdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
  toast: 100,  // ✅ Fixed: matches semanticTokens.zIndex.toast = 'z-[100]' (above modals/menus)
  max: 9999
} as const;

/**
 * Standardized motion system for consistent UX
 * 
 * Canonical durations (Page Contract Canon): 60ms / 120ms / 240ms / 480ms
 * Canonical easing: cubic-bezier(0.25, 1, 0.5, 1) = ease-standard
 * 
 * @see CANON.md Section 5.4 (Design Canon - Motion)
 * @see packages/config/theme.ts semanticTokens.motion
 */
export const MOTION = {
  // Duration scale (in seconds for framer-motion, matches Tailwind: 60/120/240/480ms)
  dur: {
    micro: 0.06,   // 60ms - micro-interactions (hover states, focus transitions)
    fast: 0.12,     // 120ms - fast transitions (exit animations, quick state changes)
    standard: 0.24, // 240ms - standard transitions (canonical, entry animations) ✅
    slow: 0.48,    // 480ms - slow transitions (page transitions, complex animations)
    // Legacy aliases (kept for backward compatibility)
    xs: 0.06,       // Alias for micro
    sm: 0.12,       // Alias for fast
    md: 0.24,       // Alias for standard
    lg: 0.48,       // Alias for slow
  },
  // Easing curves (canonical: cubic-bezier(0.25, 1, 0.5, 1))
  ease: {
    standard: [0.25, 1, 0.5, 1] as [number, number, number, number], // Canonical easing
    enter: [0.25, 1, 0.5, 1] as [number, number, number, number],    // Entry animations
    exit: [0.25, 1, 0.5, 1] as [number, number, number, number],    // Exit animations
  } as const,
} as const;

/**
 * Predefined transition presets for common animations
 * All transitions respect prefers-reduced-motion via useReducedMotion hook
 */
export const TRANSITIONS = {
  // Entry animations (standard duration: 240ms)
  enter: {
    duration: MOTION.dur.standard,
    ease: MOTION.ease.enter
  } satisfies Transition,

  // Exit animations (fast duration: 120ms)
  exit: {
    duration: MOTION.dur.fast,
    ease: MOTION.ease.exit
  } satisfies Transition,

  // Micro-interactions (60ms for hover/focus)
  micro: {
    duration: MOTION.dur.micro,
    ease: MOTION.ease.standard,
  } satisfies Transition,

  // Spring-based animations (for interactive elements)
  springSnappy: {
    type: "spring",
    stiffness: 400,
    damping: 40,
    duration: MOTION.dur.micro
  } satisfies Transition,

  // Tween-based fast transitions
  tweenFast: {
    type: "tween",
    duration: MOTION.dur.fast,
    ease: MOTION.ease.standard
  } satisfies Transition,

  // Legacy aliases (kept for backward compatibility)
  fast: { duration: MOTION.dur.fast, ease: MOTION.ease.standard } satisfies Transition,
  slow: { duration: MOTION.dur.slow, ease: MOTION.ease.standard } satisfies Transition,
} as const;

/**
 * Page transition variants for tab/route changes
 * Uses canonical slow duration: 480ms for smooth page transitions
 *
 * CANON Rule: Use for tab switching, route transitions, wizard steps
 * Pattern: fade + subtle slide (12px) for professional feel
 *
 * @example
 * <AnimatePresence mode="wait">
 *   <motion.div key={activeTab} variants={PAGE_TRANSITION_VARIANTS} initial="initial" animate="animate" exit="exit">
 *     {content}
 *   </motion.div>
 * </AnimatePresence>
 */
export const PAGE_TRANSITION_VARIANTS = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION.dur.slow, ease: MOTION.ease.standard } as Transition
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: MOTION.dur.slow, ease: MOTION.ease.standard } as Transition
  },
} satisfies Variants;

/**
 * Row animation variants for table enter/exit
 * Uses canonical durations: 240ms entry, 120ms exit
 */
export const ROW_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: TRANSITIONS.enter },
  exit: { opacity: 0, y: -8, transition: TRANSITIONS.exit },
} satisfies Variants;

/**
 * Context menu animation variants
 * Uses canonical durations: 240ms entry, 120ms exit
 */
export const MENU_VARIANTS = {
  initial: { opacity: 0, scale: 0.96, y: -8 },
  animate: { opacity: 1, scale: 1, y: 0, transition: TRANSITIONS.enter },
  exit: { opacity: 0, scale: 0.96, y: -8, transition: TRANSITIONS.exit },
} satisfies Variants;

/**
 * Search palette animation variants
 * Uses canonical durations: 240ms entry, 120ms exit
 */
export const PALETTE_VARIANTS = {
  initial: { opacity: 0, scale: 0.98 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: MOTION.dur.standard, ease: MOTION.ease.enter } as Transition
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.exit } as Transition
  },
} satisfies Variants;

/**
 * Staggered list container variants
 */
export const LIST_STAGGER = {
  staggerChildren: 0.03,
  delayChildren: 0.05
} as const;

/**
 * Search result item variants
 * Uses canonical durations: 240ms entry, 120ms exit
 */
export const ITEM_VARIANTS = {
  initial: { opacity: 0, y: 4 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION.dur.standard, ease: MOTION.ease.enter }
  },
  exit: {
    opacity: 0,
    y: 2,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.exit }
  },
} satisfies Variants;

/**
 * Modal animation variants
 * Uses canonical durations: 240ms entry, 120ms exit
 */
export const MODAL_VARIANTS = {
  initial: { opacity: 0, scale: 0.98 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: MOTION.dur.standard, ease: MOTION.ease.enter } as Transition
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.exit } as Transition
  },
} satisfies Variants;

/**
 * Modal backdrop variants
 * Uses canonical durations: 120ms (fast for backdrop)
 */
export const BACKDROP_VARIANTS = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.enter } as Transition
  },
  exit: {
    opacity: 0,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.exit } as Transition
  },
} satisfies Variants;

/**
 * Toast animation variants
 * Uses canonical durations: 240ms entry, 120ms exit
 */
export const TOAST_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION.dur.standard, ease: MOTION.ease.enter } as Transition
  },
  exit: {
    opacity: 0,
    y: 4,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.exit } as Transition
  },
} satisfies Variants;

/**
 * Tooltip animation variants
 * Uses canonical durations: 120ms (fast for tooltips)
 */
export const TOOLTIP_VARIANTS = {
  initial: { opacity: 0, y: 4 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.enter }
  },
  exit: {
    opacity: 0,
    y: 4,
    transition: { duration: MOTION.dur.fast, ease: MOTION.ease.exit }
  },
} satisfies Variants;

/**
 * Helper to get motion props that respect reduced motion preference
 * Returns empty object if reduced motion is preferred or element is not interactive
 */
export function getHoverMotion(
  opts: { reduced: boolean; interactive?: boolean }
): Partial<MotionProps> {
  if (opts.reduced || !opts.interactive) return {};
  return {
    whileHover: { scale: 1.02 },
    transition: TRANSITIONS.springSnappy
  };
}

/**
 * Hook to detect reduced motion preference
 * Returns true if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get motion variants that respect prefers-reduced-motion
 * Returns simplified variants (no animation) if reduced motion is preferred
 */
export function getMotionVariants<T extends Variants>(
  variants: T,
  reduced: boolean
): T {
  if (reduced) {
    return {
      initial: {},
      animate: {},
      exit: {},
    } as unknown as T;
  }
  return variants;
}

/**
 * Get transition that respects prefers-reduced-motion
 * Returns instant transition (duration: 0) if reduced motion is preferred
 */
export function getMotionTransition(
  transition: Transition,
  reduced: boolean
): Transition {
  if (reduced) {
    return { duration: 0 };
  }
  return transition;
}

/**
 * Tailwind class helpers for motion tokens
 * These match semanticTokens.motion from packages/config/theme.ts
 */
export const MOTION_CLASSES = {
  // Duration-based transitions (using Tailwind config values)
  micro: 'transition-all duration-60 ease-standard',      // 60ms
  fast: 'transition-all duration-120 ease-standard',      // 120ms
  standard: 'transition-all duration-240 ease-standard',  // 240ms (canonical)
  slow: 'transition-all duration-480 ease-standard',      // 480ms

  // Specific animations (entry)
  fadeIn: 'animate-in fade-in duration-240 ease-standard',
  slideUp: 'animate-in slide-in-from-bottom-2 duration-240 ease-standard',
  slideDown: 'animate-in slide-in-from-top-2 duration-240 ease-standard',
  scaleIn: 'animate-in zoom-in-95 duration-240 ease-standard',

  // Specific animations (exit)
  fadeOut: 'animate-out fade-out duration-120 ease-standard',
  slideOut: 'animate-out slide-out-to-bottom-2 duration-120 ease-standard',
  scaleOut: 'animate-out zoom-out-95 duration-120 ease-standard',

  // Reduced motion support (CSS)
  reduced: 'motion-reduce:duration-0 motion-reduce:transition-none',
} as const;